import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpService } from 'services/http/http.service';
import {
  buildQueryParams,
  createFileUploadHeaders,
  createQueryParam,
  mapFieldTypeToSubType,
  sortItemByName,
  updateEducStepResponse,
} from 'utils/helpers';
import { constants } from 'constants/constants';
import { Observable } from 'rxjs';
import { ShowIfHttpError } from '@certemy/common';
import { ConfirmThirdPartyPaymentInterface } from './step.interface';
import { DynamicFormInfoToSave } from 'components/modal-windows/step-details/dynamic-details/dynamic-details.constants';
import { TestRegistrationInfoToSave } from 'components/modal-windows/step-details/test-registration-details/test-registration-details.constants';
import _get from 'lodash-es/get';
import _set from 'lodash-es/set';
import _pick from 'lodash-es/pick';
import { DynamicForm } from 'components/multiple-dynamic-form/multiple-dynamic-form.interface';
import { DynamicFormField } from 'interfaces/dynamic-form-field.interface';
import {
  OnboardingCompletePayload,
  OnboardingStepAdditionalData,
} from 'components/modal-windows/step-details/onboarding-details/onboarding-details.constant';
import {
  OnboardingPSVStepAdditionalData,
  OnboardingPsvVerificationCompletionBody,
} from 'features/workflow-module/components/onboarding-psv-step-form/onboarding-psv-step-form.constants';
import { JobDetailsNote } from 'pages/verifier/details/job-details.interface';
import _size from 'lodash-es/size';

@Injectable()
export class StepService {
  readonly apiEndpointParts = {
    signUpPay: '/sign-up-pay',
    signUpFree: '/sign-up-free',
  };

  constructor(private http: HttpService) {}

  uploadDynamicStepEvidenceDocuments(data) {
    const options = createFileUploadHeaders();
    return this.http.post(`/dynamic_form/evidence`, data, options);
  }

  getDynamicFormData(id) {
    return this.http.get(`/dynamic_form/${id}`).pipe(
      map(response => {
        const fields = _get(response, 'fields');
        if (fields) {
          response.fields = fields.map(mapFieldTypeToSubType);
        }
        return response;
      }),
    );
  }

  getTestRegistrationFormData(stepId) {
    return this.http.get(`/step-instance/profile/${stepId}`);
  }

  @ShowIfHttpError('An error occurred while saving dynamic form')
  submitDynamicForm(formData: DynamicFormInfoToSave) {
    return this.http.post(`/dynamic_form`, formData);
  }

  @ShowIfHttpError('An error occurred while deleting document')
  removeDocument(docId: number) {
    return this.http.delete(`/evidence-file/${docId}`);
  }

  submitStepWithSpecificTypePayload(stepId: number, data: TestRegistrationInfoToSave | OnboardingCompletePayload) {
    return this.http.post(`/step-instance/${stepId}/complete`, data);
  }

  submitRegistrationForm({ stepInstanceId, fields }) {
    return this.http.post(`/certification_step_instance/${stepInstanceId}/registration-form`, fields);
  }

  @ShowIfHttpError('An error occurred while uploading the document(s). Please, try again')
  uploadFeesStepEvidenceDocuments(step_id, data) {
    const options = createFileUploadHeaders();
    return this.http.post(`/evidence_file/${step_id}`, data, options);
  }

  @ShowIfHttpError('An error occurred getting step')
  getCertificateStepInstance(id): Observable<any> {
    return this.http.get(`/certification_step_instance/${id}`).pipe(
      map(response => {
        const dynamicFormsInContent = [
          'forms',
          'onboardingForm.userInformation',
          'onboardingForm.jurisdictionInformation',
          'onboardingForm.licenseInformation',
          'onboardingForm.additionalInformation',
        ];
        dynamicFormsInContent.forEach(param => {
          const formsPath = `result.content.${param}`;
          const forms: DynamicForm[] = _get(response, formsPath);
          if (forms && forms.length) {
            _set(
              response,
              formsPath,
              forms.map((form: DynamicForm) => {
                return {
                  ...form,
                  fields: form.fields.map(mapFieldTypeToSubType),
                };
              }),
            );
          }
        });
        return response;
      }),
    );
  }

  getCertificateStep(id): Observable<any> {
    return this.getCertificateStepInstance(id).pipe(
      map(res => {
        let step = res.result;
        const { IMPOSED, PENDING_VERIFICATION, COMPLETE, REJECT, DISABLED, ARCHIVED } = constants.CERTIFICATE_STATUSES;
        const isCertificateFileUploaded = step.certification_status === PENDING_VERIFICATION && step.is_disabled;
        step._userActionsDisabled =
          isCertificateFileUploaded ||
          step.certification_status === IMPOSED ||
          step.certification_status === COMPLETE ||
          step.certification_status === REJECT ||
            step.certification_status === DISABLED ||
            step.certification_status === ARCHIVED;
        step = updateEducStepResponse(step);
        return step;
      }),
    );
  }

  @ShowIfHttpError('An error occurred. Please try again later.')
  signUpToFreeInstance(eItem_id, eItem_type) {
    return this.http.post(`/${eItem_type}/${eItem_id}${this.apiEndpointParts.signUpFree}`, '');
  }

  uploadCourseOrWebProof(eItem_id, eItem_type, data) {
    const options = createFileUploadHeaders();
    return this.http.post(`/${eItem_type}/${eItem_id}/complete`, data, options);
  }

  submitEducationReqiurement(data) {
    const options = createFileUploadHeaders();
    return this.http.post('/education/complete_requirement', data, options);
  }

  submitLogStep(stepId) {
    return this.http.post(`/certification_step_instance/${stepId}/verification`, {});
  }

  getRejectComments(id) {
    return this.http.get(`/certification_step_instance/${id}/reject_comments`).pipe(map(data => data.result.comments));
  }

  @ShowIfHttpError('An error occurred while submitting your comment. Please, try again')
  confirmThirdPartyPayment(step_instance_id: number, data: ConfirmThirdPartyPaymentInterface) {
    return this.http.post(`/certification_step_instance/${step_instance_id}/paid-by-third-party`, data);
  }

  @ShowIfHttpError('An error occurred while resetting step. Please, try again')
  resetStep(stepId, submitDataId = null) {
    return this.http.post(`/step-instance/${stepId}/reset`, { submitDataId });
  }

  mapFormInfoFields(
    stepFields: DynamicFormField[],
    formInfo,
    onlyStepField = false,
    additionalMappedStepFields: string[] = [],
  ) {
    const maps = [
      'minFieldValue',
      'maxFieldValue',
      'minDate',
      'maxDate',
      'providedBy',
      'elementOptions',
      'isWatchChangeValue',
      'filledByDisabled',
      'validators',
      'fieldInstanceId',
      'isPushInitValue',
      'dependencyData',
    ];
    if (_size(additionalMappedStepFields)) {
      maps.push(...additionalMappedStepFields);
    }
    if (!stepFields.length) {
      return formInfo.fields;
    }
    const fieldsMap = stepFields.reduce((acc, item) => (acc[item.id] = item) && acc, {});

    return formInfo.fields.reduce((result, item) => {
      if (onlyStepField && !fieldsMap[item.id]) {
        return result;
      }
      const addFields = _pick(fieldsMap[item.id] || {}, maps);
      return [
        ...result,
        {
          ...item,
          ...addFields,
        },
      ];
    }, []);
  }

  getFieldInstanceOptions(fieldInstanceId, filters) {
    const queryParams = buildQueryParams([createQueryParam('filters', filters)]);
    return this.http
      .get(`/dynamic-form-instance/field-instance/${fieldInstanceId + queryParams}`)
      .pipe(map(response => (response && response.length ? sortItemByName([...response]) : response)));
  }

  getStateOptionsFromField(fieldId, countryName) {
    const queryParams = buildQueryParams([createQueryParam('filters', countryName)]);
    return this.http.get(`/dynamic-form-instance/form-field/${fieldId + queryParams}`);
  }

  getLicenseTypeFieldOptions(certificationId, filters): Observable<OnboardingStepAdditionalData> {
    const queryParams = buildQueryParams([createQueryParam('filters', filters)]);
    return this.http.get(`/license-type/${certificationId}/options${queryParams}`);
  }

  @ShowIfHttpError('An error occurred while getting PSV step data.')
  getPsvStatus(params): Observable<OnboardingPSVStepAdditionalData> {
    const queryParams = buildQueryParams(
      Object.keys(params).map(key => {
        return createQueryParam(key, params[key]);
      }, []),
    );
    return this.http.get(`/onboarding-automation/psv-status${queryParams}`);
  }

  @ShowIfHttpError('An error occurred while flow completion.')
  getStepInstanceNotes(stepInstanceId: number): Observable<JobDetailsNote[]> {
    return this.http.get(`/step-instance/${stepInstanceId}/notes`).pipe(map(response => _get(response, 'notes', [])));
  }

  @ShowIfHttpError('An error occurred while flow completion.')
  flowCompletion(payload: OnboardingPsvVerificationCompletionBody): Observable<any> {
    return this.http.post(`/step-instance/flow-completion`, payload);
  }
}
